import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import { exhaustive } from "exhaustive"
import { isString, sortBy } from "lodash"
import { FC, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { AbsolutCentered } from "../../AbsolutCentered/AbsolutCentered"
import { useClient } from "../../Client/ClientAndUserProvider"
import { SelectedConsumerUser } from "../../Client/ConsumerContext"
import {
	ContactPersonSchema,
	ContactPersonType,
	NewContactPersonRequest,
} from "../../CustomerPortal/AddEditContactPersonModal/AddEditContactPersonModal"
import {
	GetContactPerson,
	GetContactPersonDefinitionResponse,
} from "../../CustomerPortal/ConsumerContactPersons/ConsumerContactPersons"
import consumerInformationStyle from "../../CustomerPortal/CustomerPortalConsumerInformation/CustomerPortalConsumerInformation.module.css"
import { GetUser } from "../../CustomerPortal/Users/Users"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { RelativeCentered } from "../../RelativeCentered/RelativeCentered"
import { cls } from "../../Shared/cls"
import { FieldModuleBox } from "../Components/FieldModuleBox/FieldModuleBox"
import { FieldModuleBoxWrapper } from "../Components/FieldModuleBox/FieldModuleBoxWrapper"
import { AccentButton, DenyButton, FinalizeButton } from "../Components/Form/Buttons/Buttons"
import { formatPhoneNumberForSweden } from "../Components/Form/constants"
import { MultiStateSwitch } from "../Components/Form/MultiStateSwitch/MultiStateSwitch"
import { ModulChooserButton } from "../Components/ModulChooserButton/ModulChooserButton"
import { ModulePopup, removeModalOpenClass } from "../Components/ModulePopup/ModulePopup"
import { MbtH5 } from "../Components/Text/MbtH5/MbtH5"
import { SbtH4 } from "../Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "../Components/Text/SbtInvalid/SbtRHFError"
import { convertTextToInitials } from "../Helpers"
import style from "./ClientSelectActiveConsumerUserModal.module.css"

export type ClientSelectActiveConsumerUserConsumerDetails = {
	id: string
	name: string
	identifier: string
	profilePicture?: string
}

export type ClientSelectActiveConsumerUserModalProps = {
	consumer: ClientSelectActiveConsumerUserConsumerDetails
	selectedUser: string | SelectedConsumerUser | null
	users: GetUser[]
	onClose: () => void
	onDone: (user: string | SelectedConsumerUser) => void
}

export enum SelectActiveConsumerUserView {
	RealUsers = "EditTag",
	ExistingContactPersons = "ExistingContactPersons",
}

export const ClientSelectActiveConsumerUserModal: FC<ClientSelectActiveConsumerUserModalProps> = ({
	consumer,
	selectedUser: defaultSelectedUser,
	users,
	onClose: extOnClose,
	onDone: extOnDone,
}) => {
	const client = useClient()
	const permissions = usePermissions()

	const [selectedUser, setSelectedUser] = useState<string | SelectedConsumerUser | null>(defaultSelectedUser)
	const [, setTick] = useState(0)

	const [contactPersons, setContactPersons] = useState<GetContactPerson[] | null>(null)
	const [addContactPerson, setAddContactPerson] = useState(false)

	const [isSubmittingCreateContactPerson, setIsSubmittingCreateContactPerson] = useState(false)
	const [createContactPersonFailed, setCreateContactPersonFailed] = useState(false)

	const [selectedView, setSelectedView] = useState<SelectActiveConsumerUserView>(() => {
		if (isString(selectedUser)) {
			return SelectActiveConsumerUserView.RealUsers
		} else if (!!selectedUser) {
			return SelectActiveConsumerUserView.ExistingContactPersons
		} else {
			return SelectActiveConsumerUserView.RealUsers
		}
	})

	const {
		register,
		formState: { errors, isValid },
		reset,
		getValues,
	} = useForm<ContactPersonType>({
		mode: "onChange",
		resolver: async (data, context, options) => {
			return zodResolver(ContactPersonSchema)(data, context, options)
		},
	})

	useEffect(() => {
		fetchContactPersons()
	}, [])

	function createAndSelectNewContactPerson() {
		if (!isValid) {
			return Promise.resolve()
		}

		const body: NewContactPersonRequest = {
			contactPerson: {
				name: getValues("name"),
				phone: getValues("phone"),
				email: getValues("email"),
			},
		}

		setIsSubmittingCreateContactPerson(true)
		setCreateContactPersonFailed(false)

		return API.postWithRetries<GetContactPerson, NewContactPersonRequest>(
			`/customer-portal/contact-person-definitions-v1/${client.identifier}/${consumer.id}`,
			body,
		)
			.then((res: GetContactPerson) => {
				onClose()
				extOnDone({
					id: res.id,
					name: res.name,
					email: res.email,
					phone: res.phone,
				})
			})
			.catch(() => {
				setIsSubmittingCreateContactPerson(false)
				setCreateContactPersonFailed(true)
			})
	}

	function fetchContactPersons() {
		API.getWithRetries<GetContactPersonDefinitionResponse>(
			`/customer-portal/contact-person-definitions-v1/${client.identifier}/${consumer.id}`,
			true,
		)
			.then((res) => {
				setContactPersons(
					sortBy(
						res.contactPersons.filter((y) => y.type === "ContactPerson"),
						(x) => x.name.toLocaleLowerCase(),
					),
				)
			})
			.catch(() => {
				setContactPersons([])
			})
	}

	function onClose() {
		removeModalOpenClass()
		extOnClose()
	}

	function onDone() {
		if (!selectedUser) {
			return
		}

		onClose()
		extOnDone(selectedUser)
	}

	function addContactPersonFields(): JSX.Element {
		return (
			<form className={style.tempUserForm}>
				<label>
					<SbtH4>Namn*</SbtH4>
					<input className={style.input} {...register("name")} placeholder="Anders Andersson" />
					<SbtRHFError error={errors.name} />
				</label>
				<label>
					<SbtH4>Telefonnummer*</SbtH4>
					<input className={style.input} {...register("phone")} placeholder="070 XXX XX XX" />
					<SbtRHFError error={errors.phone} />
				</label>
				<label>
					<SbtH4>E-post</SbtH4>
					<input className={style.input} {...register("email")} placeholder="exempel@exempel.se" />
					<SbtRHFError error={errors.email} />
				</label>
				<span style={{ fontSize: "16px", color: "var(--section-background-text-color)" }}>
					För att en bekräftelse ska skickas ut behövs en e-post
				</span>
			</form>
		)
	}

	function realUsersFields(): JSX.Element {
		if (users.length === 0) {
			return (
				<div style={{ marginTop: "20px", color: "var(--section-background-text-color)" }}>
					Här finns det inga användare att se än
				</div>
			)
		}

		return (
			<>
				<FieldModuleBoxWrapper className={style.fields}>
					{users.map((consumerUser) => {
						const selected = isString(selectedUser) && selectedUser === consumerUser.id
						const name =
							consumerUser.firstName || consumerUser.lastName
								? `${consumerUser.firstName} ${consumerUser.lastName}`
								: consumerUser.username
						return (
							<FieldModuleBox className={style.item} key={consumerUser.id} selected={selected}>
								<div className={style.itemContent}>
									<span className={style.profilePicture}>
										{consumerUser.profilePictureUrl ? (
											<img
												src={consumerUser.profilePictureUrl}
												onError={() => {
													consumerUser.profilePictureUrl = undefined
													setTick((tick) => tick + 1)
												}}
												alt=""
											/>
										) : (
											<AbsolutCentered>
												<span style={{ color: "white" }}>{convertTextToInitials(name)}</span>
											</AbsolutCentered>
										)}
									</span>
									<div style={{ wordBreak: "break-all" }}>
										<MbtH5>{name}</MbtH5>
										<MbtH5 styles={{ fontWeight: 300, fontSize: "16px" }}>{consumerUser.email}</MbtH5>
										<MbtH5 styles={{ fontWeight: 300, fontSize: "16px" }}>
											{formatPhoneNumberForSweden(consumerUser.phoneNumber)}
										</MbtH5>
									</div>
								</div>
								<ModulChooserButton
									selected={selected}
									onClick={() => {
										reset()
										setSelectedUser(consumerUser.id)
									}}>
									{selected ? "Vald" : "Välj"}
								</ModulChooserButton>
							</FieldModuleBox>
						)
					})}
				</FieldModuleBoxWrapper>
				<span style={{ fontSize: "16px", color: "var(--section-background-text-color)" }}>
					Användare hanteras under kundens konto
				</span>
			</>
		)
	}

	function selectContactPersonFields(): JSX.Element | null {
		if (!contactPersons) {
			return null
		}

		return (
			<FieldModuleBoxWrapper className={style.fields}>
				{contactPersons.map((contactPerson) => {
					const selected = !!selectedUser && !isString(selectedUser) && selectedUser.id === contactPerson.id
					return (
						<FieldModuleBox
							className={cls(style.item, style.contactPersonItem)}
							key={contactPerson.id}
							selected={selected}>
							<div style={{ wordBreak: "break-word", hyphens: "auto" }}>
								<MbtH5>{contactPerson.name}</MbtH5>
								<MbtH5 styles={{ fontWeight: 300, fontSize: "16px" }}>{contactPerson.email}</MbtH5>
								<MbtH5 styles={{ fontWeight: 300, fontSize: "16px" }}>
									{formatPhoneNumberForSweden(contactPerson.phone)}
								</MbtH5>
							</div>
							<ModulChooserButton
								selected={selected}
								onClick={() => {
									reset()
									setSelectedUser({
										id: contactPerson.id,
										name: contactPerson.name,
										email: contactPerson.email,
										phone: contactPerson.phone,
									})
								}}>
								{selected ? "Vald" : "Välj"}
							</ModulChooserButton>
						</FieldModuleBox>
					)
				})}
			</FieldModuleBoxWrapper>
		)
	}

	function content() {
		return exhaustive(selectedView, {
			[SelectActiveConsumerUserView.RealUsers]: () => {
				return realUsersFields()
			},

			[SelectActiveConsumerUserView.ExistingContactPersons]: () => {
				if (!contactPersons) {
					return (
						<RelativeCentered>
							<Loader />
						</RelativeCentered>
					)
				}

				if (addContactPerson) {
					return addContactPersonFields()
				}

				return selectContactPersonFields()
			},
		})
	}

	function finalizeButtonText() {
		return exhaustive(selectedView, {
			[SelectActiveConsumerUserView.RealUsers]: () => {
				return "Klar"
			},

			[SelectActiveConsumerUserView.ExistingContactPersons]: () => {
				if (addContactPerson) {
					return "Skapa och välj"
				}

				return "Klar"
			},
		})
	}

	function disableSubmit(): boolean {
		if (selectedView === SelectActiveConsumerUserView.ExistingContactPersons && addContactPerson) {
			return !isValid
		}

		return !selectedUser
	}

	function finalizeButtons() {
		return (
			<div className={style.finalizeWrapper}>
				<FinalizeButton
					disabled={disableSubmit() || isSubmittingCreateContactPerson}
					className={style.finalize}
					onClick={() => {
						if (selectedView === SelectActiveConsumerUserView.ExistingContactPersons && addContactPerson) {
							createAndSelectNewContactPerson()
						} else {
							onDone()
						}
					}}>
					{finalizeButtonText()}
					{isSubmittingCreateContactPerson ? <FontAwesomeIcon spin={true} icon={faSpinner} /> : null}
				</FinalizeButton>
				{selectedView === SelectActiveConsumerUserView.ExistingContactPersons && addContactPerson ? (
					<DenyButton
						disabled={isSubmittingCreateContactPerson}
						className={style.finalize}
						onClick={() => {
							setAddContactPerson(false)
							reset()
						}}>
						Avbryt skapande
					</DenyButton>
				) : null}
			</div>
		)
	}

	return (
		<ModulePopup className={style.modal} onClose={onClose}>
			<div className={style.wrapper}>
				<div className={style.header}>Välj beställare</div>
				{consumer ? (
					<div className={style.topRow}>
						<div className={cls(consumerInformationStyle.profilePic, style.consumerProfilePicture)}>
							{consumer.profilePicture ? (
								<img
									src={consumer.profilePicture}
									onError={() => {
										consumer.profilePicture = undefined
										setTick((tick) => tick + 1)
									}}
									alt="Profilbild"
								/>
							) : (
								<div className={consumerInformationStyle.noProfilePic}>
									<span>{convertTextToInitials(consumer.name)}</span>
								</div>
							)}
						</div>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								color: "var(--section-background-text-color)",
							}}>
							<div style={{ fontSize: "22px" }}>{consumer.name}</div>
							<div>{consumer.identifier}</div>
						</div>
						{permissions.isAllowed(PermissionAreaLocation.Consumer_Contact_Persons_Create) ? (
							<AccentButton
								disabled={isSubmittingCreateContactPerson}
								className={style.newContactPersonBtn}
								onClick={() => {
									setSelectedView(SelectActiveConsumerUserView.ExistingContactPersons)
									setAddContactPerson(true)
								}}>
								<span>Ny kontaktperson</span>
								<span style={{ fontSize: "24px" }}>+</span>
							</AccentButton>
						) : null}
					</div>
				) : null}
				{consumer ? (
					<div style={{ marginTop: "20px" }}>
						<MultiStateSwitch
							selectedValue={selectedView}
							onSelect={(option, initial) => {
								if (initial || option.key === selectedView) {
									return
								}

								setSelectedView(option.key)
							}}
							options={[
								{ key: SelectActiveConsumerUserView.RealUsers, value: "Användare" },
								{ key: SelectActiveConsumerUserView.ExistingContactPersons, value: "Kontaktpersoner" },
							]}
						/>
					</div>
				) : null}
				{content()}
				{createContactPersonFailed && addContactPerson ? (
					<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
						Något gick fel, vänligen försök igen
					</div>
				) : null}
				{finalizeButtons()}
			</div>
		</ModulePopup>
	)
}
